import React, { useRef, useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import type { TooltipProps } from 'antd';

interface EllipsisCellProps {
  /** 单元格展示内容，优先于 children */
  text?: React.ReactNode;
  /** 单元格展示内容 */
  children?: React.ReactNode;
  /** Tooltip 展示位置，默认 bottom */
  placement?: TooltipProps['placement'];
  /** 最大宽度，默认 100% */
  maxWidth?: string | number;
  /** 自定义样式 */
  style?: React.CSSProperties;
}

/**
 * 表格单元格省略展示组件
 *
 * 默认单行省略，内容超出时 hover 显示 Tooltip 完整内容；
 * 未超出时不展示 Tooltip，避免空提示干扰。
 */
const EllipsisCell: React.FC<EllipsisCellProps> = ({
  text,
  children,
  placement = 'bottom',
  maxWidth = '100%',
  style,
}) => {
  const content = children ?? text;
  const [isOverflow, setIsOverflow] = useState(false);
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = spanRef.current;
    if (el) {
      setIsOverflow(el.scrollWidth > el.clientWidth);
    }
  }, [content]);

  const span = (
    <span
      ref={spanRef}
      style={{
        display: 'inline-block',
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {content}
    </span>
  );

  return isOverflow ? (
    <Tooltip title={content} placement={placement}>
      {span}
    </Tooltip>
  ) : (
    span
  );
};

export default EllipsisCell;
